import React from 'react';
import { Box, Link } from '@mui/material';
import { useNotify } from 'react-admin';
import { getMediaUrl, MediaConfig } from '@musedrops/shared';
import { FormTextField } from '../../../shared/components/forms';
import { AudioFileField } from '../../../shared/components/forms/AudioFileField';

interface MusicUploadSectionProps {
  showSlug?: string;
  showId?: string;
  musicPrompt?: string;
  // eslint-disable-next-line no-unused-vars
  onMusicPromptChange?: (musicPrompt: string) => void;
  musicUpdatedAt?: string | null;
  onMusicDeleted?: () => void;
  // eslint-disable-next-line no-unused-vars
  onMusicUpdatedAtChanged?: (timestamp: string) => void;
}

/**
 * Background Music Upload Section Component
 *
 * Handles background music upload with music prompt field and links to music sources.
 * Uses conventional filename from MediaConfig.
 */
export const MusicUploadSection: React.FC<MusicUploadSectionProps> = ({
  showSlug,
  showId,
  musicPrompt,
  onMusicPromptChange,
  musicUpdatedAt,
  onMusicDeleted,
  onMusicUpdatedAtChanged,
}) => {
  const notify = useNotify();

  const musicFile = MediaConfig.show.backgroundMusic; // 'background-music.mp3'

  // Construct audio URL with timestamp for cache-busting
  const audioUrl = React.useMemo(() => {
    if (showSlug && musicUpdatedAt) {
      return `${getMediaUrl('shows', showSlug, musicFile)}?t=${new Date(musicUpdatedAt).getTime()}`;
    }
    if (showSlug) {
      return getMediaUrl('shows', showSlug, musicFile);
    }
    return undefined;
  }, [showSlug, musicUpdatedAt, musicFile]);

  const handleCopyMusicPrompt = async () => {
    if (!musicPrompt) {
      notify('No music prompt to copy', { type: 'warning' });
      return;
    }

    try {
      await navigator.clipboard.writeText(musicPrompt);
      notify('Music prompt copied to clipboard', { type: 'success' });
    } catch {
      notify('Failed to copy music prompt', { type: 'error' });
    }
  };

  return (
    <Box>
      {/* Music Prompt Field */}
      {onMusicPromptChange && (
        <FormTextField
          label="Music Prompt"
          value={musicPrompt || ''}
          onChange={e => onMusicPromptChange(e.target.value)}
          rows={2}
          inputProps={{ maxLength: 150 }}
          sx={{ mb: 2 }}
          onCopy={handleCopyMusicPrompt}
          copyTitle="Copy music prompt to clipboard"
        />
      )}

      {/* Audio Upload/Playback/Delete Field */}
      <AudioFileField
        label="Background Music"
        value={musicUpdatedAt ? musicFile : null} // Show filename if file exists (timestamp present)
        onUpload={() => {
          // No-op handler - just enables upload button. Timestamp update handled by onTimestampChanged.
        }}
        onDelete={onMusicDeleted}
        onTimestampChanged={onMusicUpdatedAtChanged}
        audioUrl={audioUrl}
        storageFolder="shows"
        slug={showSlug || ''}
        entityId={showId}
        tableName="shows"
        fileType="backgroundMusic"
        accept=".mp3,audio/mp3"
        uploadTitle="Upload Music (MP3)"
        helperText={
          !showSlug
            ? 'Save show details first to enable music upload'
            : undefined
        }
      />

      {/* Music Source Links */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mt: 2,
          flexWrap: 'wrap',
        }}
      >
        <Link
          href="https://licensing.jamendo.com/en/catalog"
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Jamendo
        </Link>
        <Link
          href="http://youtube.com/audiolibrary"
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          YT
        </Link>
        <Link
          href="https://freemusicarchive.org/search?adv=1&only-instrumental=1&duration_from=0&duration_to=2"
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          FMA
        </Link>
        <Link
          href="https://pixabay.com/music/search/"
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Pixabay
        </Link>
      </Box>
    </Box>
  );
};
